"use client";

import type { CSSProperties } from "react";
import Image from "@/components/CwfImage";
import { Link } from "react-router-dom";
import { usePathname } from "@/lib/cwfNavigation";
import { playUiTap } from "@/lib/audio/play";
import { useNoEnergyGate } from "@/hooks/useNoEnergyGate";

/** Reference dock: Home · Play · Events · Friends · Profile */
const NAV: readonly {
  href: string;
  label: string;
  icon?: string;
  iconSrc?: string;
  iconW?: number;
  iconH?: number;
  /** Wider bbox for landscape tab art (vs square bust icons) */
  iconWide?: boolean;
  match: (p: string) => boolean;
}[] = [
  {
    href: "/",
    label: "Home",
    iconSrc: "/home-tab-trivia.webp",
    iconW: 1536,
    iconH: 1024,
    iconWide: true,
    match: (p: string) => p === "/",
  },
  {
    href: "/matchmaking",
    label: "Play",
    iconSrc: "/play-tab-trivia.webp",
    iconW: 1536,
    iconH: 1024,
    iconWide: true,
    match: (p: string) => p.startsWith("/matchmaking"),
  },
  {
    href: "/events",
    label: "Events",
    iconSrc: "/events-tab-trivia.webp",
    iconW: 1536,
    iconH: 1024,
    iconWide: true,
    match: (p: string) => p.startsWith("/events"),
  },
  {
    href: "/friends",
    label: "Friends",
    iconSrc: "/friends-tab-trivia.webp",
    iconW: 1536,
    iconH: 1024,
    iconWide: true,
    match: (p: string) => p.startsWith("/friends"),
  },
  {
    href: "/profile",
    label: "Profile",
    iconSrc: "/profile-tab-trivia.webp",
    iconW: 1536,
    iconH: 1024,
    iconWide: true,
    match: (p: string) => p.startsWith("/profile"),
  },
];

/** Groove between two navy tabs (recessed + rim highlight) */
function TabDividerGroove() {
  return (
    <div
      className="relative w-[2px] shrink-0 self-stretch"
      aria-hidden
    >
      <div className="absolute inset-y-0 left-0 w-px bg-black/70 shadow-[1px_0_0_rgba(255,255,255,0.1)]" />
      <div className="absolute inset-y-0 right-0 w-px bg-white/10" />
    </div>
  );
}

/** Sharper edge when warm active meets cool inactive */
function TabDividerContrast() {
  return (
    <div
      className="w-px shrink-0 self-stretch bg-[#05070c] shadow-[1px_0_0_rgba(255,255,255,0.14)]"
      aria-hidden
    />
  );
}

const labelShadow =
  "[text-shadow:0_1px_0_#000,0_2px_0_rgba(0,0,0,0.55),1px_2px_0_#2a0d02]";

function tabIconShineStyle(iconSrc: string): CSSProperties {
  return {
    maskImage: `url(${iconSrc})`,
    WebkitMaskImage: `url(${iconSrc})`,
    maskMode: "alpha",
    ...( { WebkitMaskSourceType: "alpha" } as CSSProperties ),
    maskSize: "100% 100%",
    WebkitMaskSize: "100% 100%",
    maskRepeat: "no-repeat",
    WebkitMaskRepeat: "no-repeat",
    maskPosition: "center",
    WebkitMaskPosition: "center",
  };
}

export function BottomNav() {
  const pathname = usePathname() ?? "";
  const checkEnergyGate = useNoEnergyGate();
  if (
    pathname === "/play" ||
    pathname === "/login" ||
    pathname === "/matchmaking"
  )
    return null;

  return (
    <nav
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 w-full px-0 sm:px-3"
      role="navigation"
      aria-label="Main"
    >
      <div
        className="pointer-events-auto w-full overflow-visible rounded-none border-2 border-b-0 border-x-0 border-[#0a1f3d] bg-[#0b1530] pb-[env(safe-area-inset-bottom)] shadow-[0_-14px_44px_-8px_rgba(0,0,0,0.5)] sm:mx-auto sm:max-w-xl sm:rounded-t-[1.05rem] sm:border-x-2"
        data-no-ui-tap-sound
      >
        {/* Top bevel / highlight — inset so it doesn’t run edge-to-edge (icons sit above; don’t clip parent) */}
        <div
          className="mx-5 h-0.5 rounded-full bg-gradient-to-r from-cyan-200/55 via-sky-200/95 to-cyan-200/55 sm:mx-8"
          aria-hidden
        />

        <div className="flex min-h-[3.95rem] items-stretch overflow-visible sm:min-h-[4.55rem]">
          {NAV.flatMap((item, index) => {
            const active = item.match(pathname);
            const prev = index > 0 ? NAV[index - 1] : null;
            const prevActive = prev ? prev.match(pathname) : false;

            const divider =
              index > 0 ? (
                prevActive === active ? (
                  <TabDividerGroove key={`div-groove-${item.href}`} />
                ) : (
                  <TabDividerContrast key={`div-edge-${item.href}`} />
                )
              ) : null;

            const isFirst = index === 0;
            const isLast = index === NAV.length - 1;

            const tab = (
              <Link
                key={item.href}
                to={item.href}
                onClick={(e) => {
                  if (item.href === "/matchmaking" && !checkEnergyGate(e)) {
                    return;
                  }
                  try {
                    playUiTap();
                  } catch {
                    /* noop */
                  }
                }}
                className={`group relative z-0 flex min-h-0 min-w-0 flex-1 origin-bottom flex-col items-center justify-end gap-0.5 px-0.5 pb-2 pt-0.5 transition-[transform] duration-200 ease-out touch-manipulation hover:z-[25] hover:scale-[1.07] motion-reduce:transition-none motion-reduce:hover:scale-100 active:scale-[0.97] sm:px-1 sm:pb-2 sm:pt-2.5 sm:hover:scale-[1.05] ${
                  isFirst ? "sm:rounded-tl-[1.05rem]" : ""
                } ${isLast ? "sm:rounded-tr-[1.05rem]" : ""} ${
                  active
                    ? "bg-gradient-to-b from-[#fffef0] via-[#fde047] to-[#fb923c] shadow-[inset_0_2px_0_rgba(255,255,255,0.7),inset_0_-5px_12px_rgba(234,88,12,0.4)]"
                    : "bg-gradient-to-b from-[#5e9ae8] via-[#3b7dd6] to-[#1a3d7a] shadow-[inset_0_1px_0_rgba(255,255,255,0.28),inset_0_-6px_14px_rgba(0,0,0,0.22)]"
                }`}
              >
                <span
                  className="-mt-[2.85rem] mb-px flex shrink-0 items-end justify-center bg-transparent sm:-mt-1 sm:mb-0"
                  aria-hidden
                >
                  {item.iconSrc ? (
                    <span
                      className={`relative -mt-0.5 inline-flex shrink-0 items-end justify-center bg-transparent ${
                        item.iconWide
                          ? "h-[3.55rem] max-h-full max-w-[5.5rem] min-w-0 sm:h-[2.85rem] sm:max-w-[4rem]"
                          : "h-[3.95rem] w-[3.95rem] sm:h-[3.05rem] sm:w-[3.05rem]"
                      }`}
                    >
                      <Image
                        src={item.iconSrc}
                        alt=""
                        width={item.iconW ?? 512}
                        height={item.iconH ?? 512}
                        draggable={false}
                        sizes="(max-width: 640px) 96px, 72px"
                        quality={95}
                        className={`relative z-0 max-h-full bg-transparent object-contain object-center drop-shadow-[0_5px_5px_rgba(0,0,0,0.45)] ${
                          item.iconWide
                            ? "h-full w-auto max-w-full sm:drop-shadow-[0_4px_3px_rgba(0,0,0,0.42)]"
                            : "h-full w-full sm:drop-shadow-[0_4px_3px_rgba(0,0,0,0.42)]"
                        }`}
                      />
                      {/* Shine — masked to tab PNG alpha (same pattern as battle button) */}
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 z-[1] opacity-0 transition-opacity duration-200 motion-reduce:hidden group-hover:opacity-100"
                        style={tabIconShineStyle(item.iconSrc)}
                      >
                        <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/30 via-white/10 via-[35%] to-transparent" />
                        <span className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/32 via-transparent to-transparent opacity-90" />
                        <span className="pointer-events-none absolute inset-y-[10%] left-[-40%] w-[38%] -skew-x-12 bg-gradient-to-r from-transparent via-white/70 to-transparent blur-px translate-x-0 transition-transform duration-[520ms] ease-out will-change-transform motion-reduce:hidden group-hover:translate-x-[280%]" />
                      </span>
                    </span>
                  ) : (
                    <span className="text-[2.65rem] leading-none drop-shadow-[0_5px_5px_rgba(0,0,0,0.45)] sm:text-[1.55rem] sm:drop-shadow-[0_4px_3px_rgba(0,0,0,0.42)]">
                      {item.icon}
                    </span>
                  )}
                </span>
                <span
                  className={`font-display text-[0.78rem] font-black italic uppercase tracking-[0.08em] text-white sm:text-[0.62rem] ${labelShadow}`}
                >
                  {item.label}
                </span>
              </Link>
            );

            return divider ? [divider, tab] : [tab];
          })}
        </div>
      </div>
    </nav>
  );
}
